import type { LucideIcon } from 'lucide-react';
import { Inbox } from 'lucide-react';

import { cn } from '@/shared/lib/utils';

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  /** Primary action — "Add Brand", "Clear filters". */
  action?: React.ReactNode;
  className?: string;
}

/**
 * The empty state for lists and tables.
 *
 * "No data." is not an acceptable empty state (§63). An empty list has two
 * quite different causes and the user needs to know which they are looking at:
 * there is genuinely nothing yet (offer to create one), or their filters
 * excluded everything (offer to clear them). Callers pick the wording and the
 * action; this component only supplies the layout.
 */
export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}
    >
      <div className="bg-muted text-muted-foreground flex size-11 items-center justify-center rounded-full">
        <Icon className="size-5" aria-hidden />
      </div>
      <p className="mt-4 text-sm font-semibold">{title}</p>
      {description && (
        <p className="text-muted-foreground mt-1 max-w-sm text-xs leading-relaxed">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
